import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import i18n from '@/i18n';
import { add as addToOfflineQueue } from './offlineQueue';
import type { User, LoginResponse } from '@nzassa/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// ─── API Response Types ─────────────────────────────────────────
// Re-export User as ApiUser for consumer convenience
export type ApiUser = User;

export interface ApiCourse {
  id: string;
  title: string;
  description?: string | null;
  level: string;
  duration: number;
  thumbnailUrl?: string | null;
  youtubePlaylistId?: string | null;
  published: boolean;
  order: number;
  lessonsCount?: number;
}

export interface ApiSession {
  id: string;
  courseId: string;
  order: number;
  title: string;
  description?: string | null;
  videoType: string;
  youtubeVideoId?: string | null;
  r2VideoKey?: string | null;
  supportName?: string | null;
  supportType?: string | null;
  supportKey?: string | null;
  duration: number;
  livrableRequired?: boolean;
  livrablePrompt?: string | null;
  resources?: ApiResource[];
}

// Backward compat alias
export type ApiLesson = ApiSession;

export interface ApiAtelier {
  id: string;
  sessionId: string;
  title: string;
  instructions: string;
  duration?: number | null;
  supportKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResource {
  id: string;
  sessionId: string;
  name: string;
  type: string;
  driveFileId: string | null;
  externalUrl: string | null;
  order: number;
}

export interface ApiProgressRecord {
  id: string;
  userId: string;
  sessionId: string;
  // backward compat
  lessonId?: string;
  completed: boolean;
  watchTime: number;
  score: number | null;
  currentStep: string | null;
  videoDone: boolean;
  // backward compat
  resourceDone?: boolean;
  supportDone: boolean;
  quizDone: boolean;
  atelierDone: boolean;
  livrableDone: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiProgressStats {
  totalCompleted: number;
  totalInProgress: number;
  averageScore: number | null;
  totalLessons: number;
}

export interface ApiCourseProgressResponse {
  progress: ApiProgressRecord[];
  completedCount: number;
  totalLessons: number;
  completionPercentage: number;
}

export interface ApiEnrollment {
  id: string;
  userId?: string;
  courseId: string;
  enrolledAt: string;
  status: string;
  completedAt: string | null;
  course?: {
    id: string;
    title: string;
    description?: string | null;
    level: string;
    duration: number;
    thumbnailUrl?: string | null;
    order?: number;
    lessonsCount?: number;
  } | null;
  progress?: number;
  completedLessons?: number;
  totalLessons?: number;
}

export interface ApiLeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  avatarUrl: string | null;
  points: number;
}

export interface ApiBadge {
  id: string;
  userId?: string;
  type?: string;
  name: string;
  description: string;
  iconUrl: string | null;
  earnedAt?: string;
}

export interface ApiPointsHistory {
  id: string;
  userId?: string;
  action: string;
  points: number;
  earnedAt: string;
}

export interface ApiCertificate {
  id: string;
  userId?: string;
  courseId?: string;
  uniqueCode?: string;
  code?: string;
  courseName?: string;
  grade: string;
  status: string;
  issuedAt: string;
  validatedAt?: string | null;
  validatedById?: string | null;
  rejectionReason?: string | null;
  verifyUrl?: string;
}

export interface ApiNotification {
  id: string;
  userId?: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  createdAt: string;
}

export interface ApiHackathonMember {
  id: string;
  userId: string;
  name: string;
  role: string;
  joinedAt: string;
}

export interface ApiHackathonSubmission {
  id: string;
  groupId: string;
  projectTitle: string;
  projectUrl?: string | null;
  description: string;
  score?: number | null;
  feedback?: string | null;
  gradedAt?: string | null;
  submittedAt: string;
}

export interface ApiHackathonGroup {
  id: string;
  courseId: string;
  name: string;
  createdAt: string;
  memberCount?: number;
  members?: ApiHackathonMember[];
  submission?: ApiHackathonSubmission | null;
  isMember?: boolean;
}

export interface StaffUser {
  id: string;
  name: string;
  phone: string;
  role: string;
  matricule?: string;
  centreId?: string;
  classeId?: string;
  status: string;
  firstLogin: boolean;
  createdAt: string;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions extends RequestInit {
  token?: string | null;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { token, ...fetchOptions } = options;
    const method = fetchOptions.method ?? 'GET';
    const maxRetries = method === 'GET' ? 2 : 0;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Auto-inject token from store if not explicitly provided
    const storeToken = token ?? useAuthStore.getState().token;
    if (storeToken) {
      headers['Authorization'] = `Bearer ${storeToken}`;
    }

    // Offline interception: queue mutation requests when offline
    if (!navigator.onLine && MUTATION_METHODS.has(method)) {
      await addToOfflineQueue({
        url: `${this.baseUrl}${endpoint}`,
        method,
        body: (fetchOptions.body as string) ?? null,
        headers,
      });
      toast.info('Action enregistr\u00e9e hors ligne');
      return {} as T;
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
          ...fetchOptions,
          headers,
        });

        if (!response.ok) {
          // Auto-logout on 401 Unauthorized (expired token)
          if (response.status === 401 && useAuthStore.getState().isAuthenticated) {
            useAuthStore.getState().logout();
            toast.error(i18n.t('errors.session_expiree'), { id: 'session-expired' });
            window.location.href = '/connexion';
            throw new Error(i18n.t('errors.session_expiree'));
          }

          const error = await response.json().catch(() => ({
            message: i18n.t('errors.erreur_generique'),
          }));
          throw new Error(error.message || `Erreur ${response.status}`);
        }

        return response.json();
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        const isNetworkError = err instanceof TypeError;

        // Queue mutation network errors for offline replay
        if (isNetworkError && MUTATION_METHODS.has(method) && attempt >= maxRetries) {
          await addToOfflineQueue({
            url: `${this.baseUrl}${endpoint}`,
            method,
            body: (fetchOptions.body as string) ?? null,
            headers,
          });
          toast.info('Action enregistr\u00e9e hors ligne');
          return {} as T;
        }

        // Only retry on network errors (TypeError from fetch), not HTTP errors
        if (!isNetworkError || attempt >= maxRetries) throw lastError;
        // Exponential backoff: 500ms, 1500ms
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }

    throw lastError ?? new Error(i18n.t('errors.erreur_generique'));
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // ─── Typed API Methods ───────────────────────────────────────

  // Auth
  async login(matricule: string, password: string) {
    return this.post<LoginResponse>('/auth/login', { matricule, password });
  }

  async firstLogin(matricule: string, password: string, passwordConfirm: string, oldPassword?: string) {
    return this.post<LoginResponse>('/auth/first-login', { matricule, password, passwordConfirm, oldPassword });
  }

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
    return this.put<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
      newPasswordConfirm: confirmPassword,
    });
  }

  async getMe() {
    return this.get<{ user: ApiUser }>('/auth/me');
  }

  async updateProfile(data: { name: string }) {
    return this.put<{ user: ApiUser }>('/auth/profile', data);
  }

  // Courses
  async getCourses(filters?: { published?: boolean; archived?: boolean }) {
    const params = new URLSearchParams();
    if (filters?.published !== undefined) params.set('published', String(filters.published));
    if (filters?.archived !== undefined) params.set('archived', String(filters.archived));
    const qs = params.toString();
    return this.get<{ courses: ApiCourse[] }>(`/courses${qs ? `?${qs}` : ''}`);
  }

  async getCourse(courseId: string) {
    return this.get<{ course: ApiCourse }>(`/courses/${courseId}`);
  }

  async getCourseSessions(courseId: string) {
    return this.get<{ sessions: ApiSession[]; lessons: ApiSession[] }>(`/courses/${courseId}/sessions`);
  }

  async getCourseLessons(courseId: string) {
    return this.get<{ lessons: ApiSession[] }>(`/courses/${courseId}/lessons`);
  }

  // Admin session CRUD
  async getAdminCourseSessions(courseId: string) {
    return this.get<{ sessions: ApiSession[] }>(`/admin/courses/${courseId}/sessions`);
  }

  async createSession(courseId: string, data: Partial<ApiSession>) {
    return this.post<{ session: ApiSession }>(`/admin/courses/${courseId}/sessions`, data);
  }

  async updateSession(sessionId: string, data: Partial<ApiSession>) {
    return this.put<{ session: ApiSession }>(`/admin/sessions/${sessionId}`, data);
  }

  async deleteSession(sessionId: string) {
    return this.delete<{ message: string }>(`/admin/sessions/${sessionId}`);
  }

  // R2 presigned upload
  async presignVideo(sessionId: string, fileName: string) {
    return this.post<{ url: string; key: string; publicUrl: string }>(
      `/admin/sessions/${sessionId}/presign-video`,
      { fileName },
    );
  }

  async presignSupport(sessionId: string, fileName: string) {
    return this.post<{ url: string; key: string; publicUrl: string; fileName: string }>(
      `/admin/sessions/${sessionId}/presign-support`,
      { fileName },
    );
  }

  async presignGet(key: string) {
    return this.post<{ url: string }>('/r2/presign-get', { key });
  }

  /** Upload a file directly to R2 using a presigned PUT URL. Uses XHR for progress. */
  async uploadToR2(
    presignedUrl: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      // Do NOT set Content-Type header — R2 presigned URL is signed without it
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        });
      }
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`));
      });
      xhr.addEventListener('error', () => reject(new Error('R2 upload network error')));
      xhr.send(file);
    });
  }

  // Progress
  async getProgress() {
    return this.get<{ progress: ApiProgressRecord[] }>('/progress');
  }

  async getProgressStats() {
    return this.get<{ stats: ApiProgressStats }>('/progress/stats');
  }

  async getCourseProgress(courseId: string) {
    return this.get<ApiCourseProgressResponse>(`/courses/${courseId}/progress`);
  }

  async updateSessionProgress(sessionId: string, data: { completed?: boolean; watchTime?: number; videoDone?: boolean; currentStep?: string }) {
    return this.put<{ success: boolean }>(`/progress/${sessionId}`, data);
  }

  async updateLessonProgress(sessionId: string, data: { completed?: boolean; watchTime?: number }) {
    return this.updateSessionProgress(sessionId, data);
  }

  // Enrollments
  async getEnrollments() {
    return this.get<{ enrollments: ApiEnrollment[] }>('/enrollments');
  }

  async enroll(courseId: string) {
    return this.post<{ enrollment: ApiEnrollment }>('/enrollments', { courseId });
  }

  // Gamification (fallback to demo routes if /gamification/ not mounted)
  async getLeaderboard(limit?: number) {
    const qs = limit ? `?limit=${limit}` : '';
    // Try real gamification endpoint first, fallback to demo
    try {
      return await this.get<{ leaderboard: ApiLeaderboardEntry[] }>(`/gamification/leaderboard${qs}`);
    } catch {
      return this.get<{ leaderboard: ApiLeaderboardEntry[] }>(`/leaderboard${qs}`);
    }
  }

  async getBadges() {
    // Try real gamification endpoint first, fallback to demo
    try {
      return await this.get<{ badges: ApiBadge[] }>('/gamification/badges');
    } catch {
      return this.get<{ badges: ApiBadge[] }>('/badges/me');
    }
  }

  async getTotalPoints() {
    // Try real gamification endpoint first, fallback to demo
    try {
      return await this.get<{ totalPoints: number; userId: string }>('/gamification/points/total');
    } catch {
      return this.get<{ totalPoints: number; history: ApiPointsHistory[] }>('/points/me');
    }
  }

  async getPointsHistory() {
    return this.get<{ totalPoints: number; history: ApiPointsHistory[] }>('/points/me');
  }

  // Certificates
  async getCertificates() {
    // Try real route first, fallback to demo
    try {
      return await this.get<{ certificates: ApiCertificate[] }>('/certificates');
    } catch {
      return this.get<{ certificates: ApiCertificate[] }>('/certificates/me');
    }
  }

  async generateCertificate(courseId: string) {
    return this.post<{ certificate: { code: string }; verifyUrl: string }>(
      '/certificates/generate',
      { courseId },
    );
  }

  // Notifications
  async getNotifications() {
    return this.get<{ notifications: ApiNotification[]; unreadCount?: number }>('/notifications');
  }

  async markNotificationRead(id: string) {
    return this.put<{ notification: ApiNotification }>(`/notifications/${id}/read`, {});
  }

  async markAllNotificationsRead() {
    return this.put<{ message: string; count: number }>('/notifications/read-all', {});
  }

  // Settings
  async getPublicSettings() {
    return this.get<{ coachAiEnabled: boolean; sessionModeGlobal: boolean }>('/settings/public');
  }

  // Hackathon
  async getHackathonGroups(courseId?: string) {
    const qs = courseId ? `?courseId=${encodeURIComponent(courseId)}` : '';
    return this.get<{ groups: ApiHackathonGroup[] }>(`/hackathon/groups${qs}`);
  }

  async createHackathonGroup(data: { courseId: string; name: string }) {
    return this.post<{ group: ApiHackathonGroup }>('/hackathon/groups', data);
  }

  async getHackathonGroup(groupId: string) {
    return this.get<{ group: ApiHackathonGroup }>(`/hackathon/groups/${groupId}`);
  }

  async joinHackathonGroup(groupId: string, name?: string) {
    return this.post<{ group: ApiHackathonGroup }>(`/hackathon/groups/${groupId}/join`, { name });
  }

  async submitHackathonProject(
    groupId: string,
    data: { projectTitle: string; projectUrl?: string; description: string },
  ) {
    return this.post<{ submission: ApiHackathonSubmission }>(`/hackathon/groups/${groupId}/submit`, data);
  }

  async gradeHackathonSubmission(
    submissionId: string,
    data: { score: number; feedback?: string },
  ) {
    return this.put<{ submission: ApiHackathonSubmission }>(`/hackathon/submissions/${submissionId}/grade`, data);
  }

  // ── Progression steps ──────────────────────────────────────────
  async markSupportDone(sessionId: string) {
    return this.post<{ progress: ApiProgressRecord }>(`/progress/${sessionId}/support-done`);
  }

  async markAtelierDone(sessionId: string) {
    return this.post<{ progress: ApiProgressRecord }>(`/progress/${sessionId}/atelier-done`);
  }

  async getSessionAtelier(sessionId: string) {
    return this.get<{ atelier: ApiAtelier | null }>(`/sessions/${sessionId}/atelier`);
  }

  // ── Admin: quiz par session ────────────────────────────────────
  async getAdminSessionQuiz(sessionId: string) {
    return this.get<{ quiz: { id: string; questions: unknown[] } | null }>(`/admin/sessions/${sessionId}/quiz`);
  }

  async updateAdminSessionQuiz(sessionId: string, questions: unknown[]) {
    return this.put<{ quiz: { id: string; questions: unknown[] } }>(`/admin/sessions/${sessionId}/quiz`, { questions });
  }

  // ── Admin: atelier par session ─────────────────────────────────
  async getAdminSessionAtelier(sessionId: string) {
    return this.get<{ atelier: ApiAtelier | null }>(`/admin/sessions/${sessionId}/atelier`);
  }

  async upsertSessionAtelier(sessionId: string, data: { title: string; instructions: string; duration?: number | null }) {
    return this.post<{ atelier: ApiAtelier }>(`/admin/sessions/${sessionId}/atelier`, data);
  }

  // ── Admin: mentor assignments ──────────────────────────────────
  async getMentorAssignments() {
    return this.get<{ assignments: Array<{ id?: string; mentorId: string; menteeId: string; mentor?: { name: string }; mentee?: { name: string }; assignedAt?: string }> }>('/admin/mentor-assignments');
  }

  async createMentorAssignment(mentorId: string, menteeId: string) {
    return this.post<{ assignment: unknown }>('/admin/mentor-assignments', { mentorId, menteeId });
  }

  async deleteMentorAssignment(mentorId: string, menteeId: string) {
    return this.delete<{ ok: boolean }>(`/admin/mentor-assignments/${mentorId}/${menteeId}`);
  }

  // ── Admin: centres / classes ───────────────────────────────────
  async getCentres() {
    return this.get<{ centres: Array<{ id: string; name: string; code: string; region?: string | null }> }>('/admin/centres');
  }

  async createCentre(data: { name: string; code: string; region?: string }) {
    return this.post<{ centre: { id: string; name: string; code: string; region?: string | null } }>('/admin/centres', data);
  }

  async updateCentre(centreId: string, data: Partial<{ name: string; region: string }>) {
    return this.put<{ centre: { id: string; name: string; code: string; region?: string | null } }>(`/admin/centres/${centreId}`, data);
  }

  async deleteCentre(centreId: string) {
    return this.delete<{ ok: boolean }>(`/admin/centres/${centreId}`);
  }

  async getCentreClasses(centreId: string) {
    return this.get<{ classes: Array<{ id: string; centreId: string; name: string; year?: number | null }> }>(`/admin/centres/${centreId}/classes`);
  }

  async createClasse(centreId: string, data: { name: string; year?: number }) {
    return this.post<{ classe: { id: string; centreId: string; name: string; year?: number | null } }>(`/admin/centres/${centreId}/classes`, data);
  }

  async deleteClasse(classeId: string) {
    return this.delete<{ ok: boolean }>(`/admin/classes/${classeId}`);
  }

  async archiveCourse(courseId: string, archived: boolean) {
    return this.put<{ course: unknown }>(`/admin/courses/${courseId}/archive`, { archived });
  }

  // ── Admin: staff management ────────────────────────────────────
  getCentreStaff(centreId: string) {
    return this.get<{ users: StaffUser[] }>(`/admin/users?centreId=${centreId}&limit=100`);
  }

  createStaffUser(data: {
    name: string;
    phone: string;
    role: string;
    matricule?: string;
    centreId: string;
    classeId?: string;
  }) {
    return this.post<{ user: StaffUser; credentials?: { matricule?: string; tempPassword: string } }>('/admin/users', data);
  }

  updateStaffUser(userId: string, updates: {
    name?: string;
    phone?: string;
    role?: string;
    centreId?: string;
    classeId?: string;
    matricule?: string;
    status?: string;
  }) {
    return this.put<{ user: StaffUser }>(`/admin/users/${userId}`, updates);
  }

  resetUserPassword(userId: string) {
    return this.post<{ ok: boolean; user: { id: string; name: string } }>(`/admin/users/${userId}/reset-password`, {});
  }

  reassignUserData(fromUserId: string, toUserId: string) {
    return this.post<{ ok: boolean; from: string; to: string; reassigned: Record<string, number> }>(
      `/admin/users/${fromUserId}/reassign`,
      { toUserId },
    );
  }

  // ── M4 : Bilan apprenant ────────────────────────────────────────
  getMyReport() {
    return this.get<{
      completedLessons: number;
      avgScore: number;
      totalPoints: number;
      livrables: number;
      badges: number;
      attendance: number;
    }>('/reports/me');
  }

  getStudentAnalytics(studentId: string) {
    return this.get<{
      completedSessions: number;
      totalSessions: number;
      avgQuizScore: number;
      totalWatchTime: number;
      livrablesSubmitted: number;
      attendanceRate: number;
      perSession: { sessionId: string; score: number | null; completed: boolean; currentStep: string | null; watchTime: number }[];
    }>(`/analytics/student/${studentId}`);
  }

  // ── M5 : Quiz stats formateur ───────────────────────────────────
  getFormateurQuizStats(courseId: string) {
    return this.get<{
      quizStats: {
        sessionId: string;
        lessonTitle: string;
        order: number;
        attempts: number;
        avgScore: number;
        passRate: number;
      }[];
    }>(`/formateur/courses/${courseId}/quiz-stats`);
  }

  // ── M2 : Ressources additionnelles ─────────────────────────────
  getSessionResources(sessionId: string) {
    return this.get<{
      resources: { id: string; sessionId: string; name: string; type: string; externalUrl: string | null; driveFileId: string | null; order: number }[];
    }>(`/sessions/${sessionId}/resources`);
  }

  createSessionResource(sessionId: string, data: { name: string; type: string; externalUrl?: string; driveFileId?: string; order?: number }) {
    return this.post<{ resource: { id: string; name: string; type: string; externalUrl: string | null; order: number } }>(
      `/admin/sessions/${sessionId}/resources`,
      data,
    );
  }

  deleteSessionResource(resourceId: string) {
    return this.delete<{ ok: boolean }>(`/admin/resources/${resourceId}`);
  }

  // ── M8 : Notation challenges ────────────────────────────────────
  gradeSubmission(challengeId: string, submissionId: string, score: number, feedback: string) {
    return this.put<{ submission: { id: string; score: number; feedback: string } }>(
      `/challenges/${challengeId}/submissions/${submissionId}/grade`,
      { score, feedback },
    );
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
    options?: RequestOptions,
  ): Promise<T> {
    const { token, ...fetchOptions } = options || {};

    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    };

    // Auto-inject token from store if not explicitly provided
    const storeToken = token ?? useAuthStore.getState().token;
    if (storeToken) {
      headers['Authorization'] = `Bearer ${storeToken}`;
    }

    // Don't set Content-Type - browser will set it for FormData with boundary
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...fetchOptions,
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      // Auto-logout on 401 Unauthorized (expired token)
      if (response.status === 401 && useAuthStore.getState().isAuthenticated) {
        useAuthStore.getState().logout();
        toast.error('Session expirée. Veuillez vous reconnecter.', { id: 'session-expired' });
        window.location.href = '/connexion';
        throw new Error('Session expirée');
      }

      const error = await response.json().catch(() => ({
        message: 'Une erreur est survenue',
      }));
      throw new Error(error.message || `Erreur ${response.status}`);
    }

    return response.json();
  }
}

export const api = new ApiClient(API_BASE);
